import { useStore, type Atom } from 'jotai';
import { useEffect, useRef, useState } from 'react';

/** 예약된 전파를 취소하는 함수. 스케줄이 불가능한 환경이면 `undefined`(= 주기를 열지 않음 = 항상 즉시 전파). */
type Schedule = (task: () => void) => (() => void) | undefined;

/** 다음 페인트에 맞춰 예약한다. rAF 부재 환경(SSR·비 DOM 테스트)에서는 주기를 열지 않는다. */
const scheduleFrame: Schedule = (task) => {
  if (typeof requestAnimationFrame !== 'function') return undefined;
  const id = requestAnimationFrame(() => task());
  return () => cancelAnimationFrame(id);
};

/** 벽시계 기준으로 `delayMs` 뒤에 예약한다. */
const createDelaySchedule =
  (delayMs: number): Schedule =>
  (task) => {
    const id = setTimeout(task, delayMs);
    return () => clearTimeout(id);
  };

/**
 * 전파를 주기당 1회로 캡핑하는 스로틀러. 주기 밖의 첫 알림은 즉시 전파하고, 열린 주기 안의 알림은 병합해
 * 주기 끝에 마지막 것만 전파한 뒤 주기를 재개한다(연속 스트림에서 주기당 1회를 유지하기 위함).
 *
 * 스케줄링 수단만 주입받으므로 rAF·타이머 어느 쪽으로도 같은 의미론을 갖는다.
 * es-toolkit `throttle`로 대체하지 않는 이유: 시간 기반이라 페인트 주기에 맞출 수 없고, 브라우저가 숨은 탭에서
 * rAF를 멈추는 성질(= 안 보이는 UI의 리렌더가 자동으로 멈춤)도 잃는다.
 */
function createThrottler(propagate: () => void, schedule: Schedule) {
  let cancelScheduled: (() => void) | undefined; // undefined면 "열린 주기 없음" = 다음 알림은 즉시 전파
  let merged = false; // 열린 주기 동안 병합(전파 보류)된 알림이 있는지

  const openWindow = () => {
    cancelScheduled = schedule(() => {
      cancelScheduled = undefined;
      if (!merged) return; // 병합분 없음 → 주기를 닫아 다음 알림이 즉시 전파되게 한다
      merged = false;
      propagate();
      openWindow(); // 값을 냈으니 주기를 재개 — 주기당 1회를 유지한다
    });
  };

  return {
    /** 값이 바뀌었음을 알린다. 주기 밖이면 즉시, 주기 안이면 병합해 주기 끝에 전파한다. */
    notify: () => {
      if (cancelScheduled != null) {
        merged = true;
        return;
      }
      propagate();
      openWindow();
    },
    /** 예약된 전파를 취소한다(unmount·effect 재실행·탭 숨김 시). */
    cancel: () => {
      cancelScheduled?.();
      cancelScheduled = undefined;
      merged = false;
    },
  };
}

const isDocumentHidden = () => typeof document !== 'undefined' && document.hidden;

/**
 * atom 값을 구독하되 컴포넌트로의 전파(리렌더)를 스로틀해 반환하는 공통 구현.
 *
 * `delayMs`가 없으면 rAF(프레임당 1회), 있으면 벽시계(`delayMs`당 1회)로 캡핑한다.
 * 타이머는 숨은 탭에서도 계속 돌기 때문에, 시간 기반 경로일 때만 `visibilitychange`로 명시적으로
 * 멈추고 복귀 시 최신값으로 동기화한다. rAF 경로는 브라우저가 프레임 자체를 멈추므로 그 처리가 필요 없다.
 */
function useThrottledAtomValue<T>(anAtom: Atom<T>, delayMs?: number): T {
  const store = useStore();
  // 값과 그 출처(store·atom)를 함께 들고 있어야, 출처가 교체된 렌더에서 이전 값을 반환하지 않는다.
  // jotai `useAtomValue`가 쓰는 방식과 동일하다.
  const [state, setState] = useState(() => ({ value: store.get(anAtom), store, anAtom }));
  const latestRef = useRef(state.value);

  let value = state.value;
  if (state.store !== store || state.anAtom !== anAtom) {
    // 출처가 바뀌었으면 effect(커밋 이후)를 기다리지 않고 이 렌더에서 바로 새 값을 반환한다.
    // 렌더 중 setState는 React가 자식 렌더 전에 즉시 재실행하는 공식 패턴(파생 state 조정)이다.
    value = store.get(anAtom);
    latestRef.current = value;
    setState({ value, store, anAtom });
  }

  useEffect(() => {
    // state가 복합 객체라 매번 새 참조를 만들면 값이 같아도 리렌더된다 → 동등하면 prev를 그대로 반환해
    // React의 bail-out을 유지한다(jotai `useAtomValue`의 reducer와 동일한 비교).
    const propagate = () =>
      setState((prev) =>
        Object.is(prev.value, latestRef.current) && prev.store === store && prev.anAtom === anAtom
          ? prev
          : { value: latestRef.current, store, anAtom },
      );

    /** 스로틀을 건너뛰고 최신 source 값을 즉시 반영한다. */
    const syncNow = () => {
      latestRef.current = store.get(anAtom);
      propagate();
    };

    // render→commit 사이에 값이 바뀌었을 수 있으니 재동기화한다.
    syncNow();

    const pauseWhileHidden = delayMs != null;
    const throttler = createThrottler(propagate, delayMs == null ? scheduleFrame : createDelaySchedule(delayMs));

    const unsubscribe = store.sub(anAtom, () => {
      latestRef.current = store.get(anAtom);
      // 숨은 탭에서는 전파를 예약하지 않는다. 값은 ref에 최신으로 남아 복귀 시 한 번에 반영된다.
      if (pauseWhileHidden && isDocumentHidden()) return;
      throttler.notify();
    });

    const handleVisibilityChange = () => {
      // 숨는 순간: 열린 주기의 trailing 전파를 취소해 안 보이는 UI의 리렌더를 막는다.
      if (isDocumentHidden()) {
        throttler.cancel();
        return;
      }
      // 복귀하는 순간: 다음 tick을 기다리지 않고 최신값으로 맞춘다(그동안 갱신이 끊겼을 수도 있다).
      syncNow();
    };
    const observesVisibility = pauseWhileHidden && typeof document !== 'undefined';
    if (observesVisibility) document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      unsubscribe();
      throttler.cancel();
      if (observesVisibility) document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [store, anAtom, delayMs]);

  return value;
}

/**
 * atom 값을 구독하되, 컴포넌트로의 전파(리렌더)를 **프레임당 1회**(requestAnimationFrame)로 캡핑해 반환한다.
 * 소켓 tick처럼 프레임 레이트(≈16.6ms)보다 잦게 갱신되는 표시값의 리렌더를 줄이는 용도.
 *
 * - **첫 값은 즉시**(leading): 조용한 상태(열린 주기 없음)에서의 변경은 지연 없이 바로 전파된다.
 * - **이후는 스로틀**(trailing): 열린 주기 안의 변경은 병합돼 프레임 끝에 마지막 값 하나만 전파된다.
 *
 * 숨은 탭에서는 브라우저가 rAF를 멈추므로, 주기가 한 번 열린 뒤로는 전파도 함께 멈춘다 — 안 보이는 UI의 리렌더를
 * 아끼는 의도된 동작이다. (주기가 닫힌 상태에서 숨겨진 경우, 그 뒤 첫 갱신 1회만 leading으로 전파된 뒤 얼어붙는다.)
 * 그래서 이 훅은 `visibilitychange`를 구독하지 않는다 — 소비처마다 리스너를 다는 비용을 피한 트레이드오프다.
 * 숨은 탭에서도 갱신돼야 하는 값(예: 탭 제목에 노출되는 가격)에는 `atomWithRafThrottle`의
 * `forceRefreshPeriod`를 쓸 것.
 *
 * 프레임 레이트보다 **더 성기게** 캡핑하고 싶으면 {@link useTimeThrottledAtomValue}를 쓸 것.
 *
 * `atomWithRafThrottle`와 목적은 같지만 스로틀 위치가 다르다: 여기선 스로틀이 **표시 컴포넌트의 소비 시점**에만
 * 존재한다. 원본 atom은 그대로 단일 진실원이라 스로틀된 값이 그래프에 새지 않고, 주문/검증 경로가 오독할
 * 대상 자체가 없다. 캡핑은 이 hook을 쓰는 컴포넌트에만 적용된다.
 *
 * **동기 atom 전용** — async atom은 넣지 말 것. `useAtomValue`는 `Awaited<Value>`로 unwrap(suspend)하지만
 * 이 훅은 `store.get` 결과를 그대로 반환하므로 Promise 자체가 나온다.
 *
 * 표시 전용으로만 쓸 것 — 반환값은 최대 1프레임 지연될 수 있다. 정확성이 필요한 계산은 원본 atom을
 * (`useAtomValue`/`store.get`으로) 직접 읽어야 한다. 소비처가 여럿이면 각자 독립적으로 스로틀되며 값
 * 스냅샷은 공유되지 않는다 — 다수 소비처 일관성이나 파생 atom 계산이 필요하면 `atomWithRafThrottle`을 쓸 것.
 *
 * **반환값을 담는 변수도 `throttled*`로 이름 붙일 것** — `atomWithRafThrottle`의 `throttled*Atom` 규칙과
 * 같은 이유다. 호출 라인을 벗어나면 훅 이름이 보이지 않으므로, 지연 가능성은 변수명에 남아야 한다.
 * 주문 제출·검증(수량/가격 계산, payload 빌드) 경로에서는 이 훅의 반환값을 쓰지 말 것 — 스로틀되지 않은
 * 원본 atom을 직접 읽어야 한다.
 *
 * ```ts
 * const throttledPrice = useRafThrottledAtomValue(lastPriceAtom); // 표시용 O
 * const price = useAtomValue(lastPriceAtom);                      // 주문 검증용 O
 * ```
 */
export function useRafThrottledAtomValue<T>(anAtom: Atom<T>): T {
  return useThrottledAtomValue(anAtom);
}

/**
 * atom 값을 구독하되, 컴포넌트로의 전파(리렌더)를 **`delayMs`당 1회**(벽시계 기준)로 캡핑해 반환한다.
 * 프레임 레이트보다 **더 성기게** 갱신해도 되는 표시값(예: 카드의 PnL을 200ms마다)에 쓴다.
 * 프레임 단위로 부드럽게 따라가야 하는 값에는 {@link useRafThrottledAtomValue}를 쓸 것.
 *
 * - **첫 값은 즉시**(leading), 이후 `delayMs` 창 안의 변경은 병합돼 창 끝에 마지막 값 하나만 전파된다.
 * - 페인트 주기와 무관하다 — 여러 소비처의 전파 시점이 프레임에 정렬되지 않는다.
 * - 타이머는 숨은 탭에서도 돌기 때문에, 탭이 숨으면 전파를 멈추고 복귀 시 최신값으로 즉시 동기화한다.
 *
 * 그 외 사용 규칙({@link useRafThrottledAtomValue}의 `throttled*` 네이밍, 표시 전용, 동기 atom 전용,
 * 소비처별 독립 스냅샷)은 모두 동일하게 적용된다.
 */
export function useTimeThrottledAtomValue<T>(anAtom: Atom<T>, delayMs: number): T {
  return useThrottledAtomValue(anAtom, delayMs);
}
